import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import MyLayout from '../components/MyLayout';
import { useToast } from '@/contexts/ToastContext';

const profileSchema = z.object({
  name: z.string().min(2),
  phone: z.string().min(7),
  language: z.enum(['ko', 'en']),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(6),
    newPassword: z.string().min(6),
    confirmNewPassword: z.string().min(6),
  })
  .refine((d) => d.newPassword === d.confirmNewPassword, {
    message: "Passwords don't match",
    path: ['confirmNewPassword'],
  });

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export default function MyProfilePage() {
  const { t, i18n } = useTranslation();
  const { currentUser, updateProfile, changePassword } = useAuth();
  const { showToast } = useToast();
  const [pwError, setPwError] = useState('');

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: currentUser?.name || '',
      phone: currentUser?.phone?.replace('+880 ', '') || '',
      language: (currentUser?.language as 'ko' | 'en') || 'ko',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const onProfileSubmit = async (data: ProfileForm) => {
    const ok = await updateProfile({
      name: data.name,
      phone: `+880 ${data.phone}`,
      language: data.language,
    });
    if (!ok) {
      showToast(t('my.profile.saveError', 'Failed to update profile'));
      return;
    }
    if (data.language !== i18n.language) {
      i18n.changeLanguage(data.language);
    }
    showToast(t('my.profile.saved', 'Profile updated'));
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    setPwError('');
    const result = await changePassword(data.currentPassword, data.newPassword);
    if (!result.ok) {
      setPwError(result.error ?? t('my.profile.passwordError', 'Failed to update password'));
      return;
    }
    showToast(t('my.profile.passwordChanged', 'Password updated'));
    passwordForm.reset();
  };

  return (
    <MyLayout title={t('my.profile.title', 'Profile Settings')}>
      <div className="flex flex-col gap-6">
        {/* Profile Form */}
        <div className="bg-white rounded-2xl p-6 border border-[#E8E8E6]">
          <h3 className="text-sm tracking-wider uppercase font-medium text-[#2A2A2A] mb-5">
            {t('my.profile.info', 'Personal Information')}
          </h3>
          <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                {t('auth.register.name', 'Name')}
              </label>
              <input
                {...profileForm.register('name')}
                className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
              />
              {profileForm.formState.errors.name && (
                <p className="mt-1 text-xs text-[#7B1113]">{profileForm.formState.errors.name.message}</p>
              )}
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                {t('auth.register.phone', 'Phone')}
              </label>
              <div className="flex">
                <span className="flex items-center px-3 rounded-l-xl border border-r-0 border-[#E8E8E6] bg-[#F0EFED] text-sm text-[#8A8A8A]">
                  +880
                </span>
                <input
                  {...profileForm.register('phone')}
                  className="flex-1 px-4 py-3 rounded-r-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                />
              </div>
              {profileForm.formState.errors.phone && (
                <p className="mt-1 text-xs text-[#7B1113]">{profileForm.formState.errors.phone.message}</p>
              )}
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                {t('my.profile.language', 'Language')}
              </label>
              <select
                {...profileForm.register('language')}
                className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors h-11 bg-white"
              >
                <option value="ko">한국어</option>
                <option value="en">English</option>
              </select>
            </div>
            <button
              type="submit"
              className="w-full sm:w-auto px-8 py-3 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer whitespace-nowrap h-12"
            >
              {t('common.save', 'Save Changes')}
            </button>
          </form>
        </div>

        {/* Password Form */}
        <div className="bg-white rounded-2xl p-6 border border-[#E8E8E6]">
          <h3 className="text-sm tracking-wider uppercase font-medium text-[#2A2A2A] mb-5">
            {t('my.profile.password', 'Change Password')}
          </h3>
          {pwError && (
            <div className="mb-4 p-3 rounded-xl bg-[#7B1113]/5 text-sm text-[#7B1113]">{pwError}</div>
          )}
          <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                {t('my.profile.currentPassword', 'Current Password')}
              </label>
              <input
                type="password"
                {...passwordForm.register('currentPassword')}
                className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
              />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                {t('my.profile.newPassword', 'New Password')}
              </label>
              <input
                type="password"
                {...passwordForm.register('newPassword')}
                className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
              />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                {t('my.profile.confirmNewPassword', 'Confirm New Password')}
              </label>
              <input
                type="password"
                {...passwordForm.register('confirmNewPassword')}
                className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
              />
              {passwordForm.formState.errors.confirmNewPassword && (
                <p className="mt-1 text-xs text-[#7B1113]">
                  {passwordForm.formState.errors.confirmNewPassword.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="w-full sm:w-auto px-8 py-3 bg-[#2A2A2A] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap h-12"
            >
              {t('my.profile.changePassword', 'Update Password')}
            </button>
          </form>
        </div>
      </div>
    </MyLayout>
  );
}